import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  X, Clock, ShieldCheck, EyeOff, CheckCircle2, RotateCw,
  ExternalLink, FileText, ScanLine, BadgeDollarSign, Zap, Users,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { fmtMoney } from "../lib/money";
import { toast } from "sonner";
import {
  AlertSeverityBadge, AlertStateBadge, AlertSourceBadge, UrgencyMeter,
} from "./AlertBadges";
import GuardianPriorityBadge from "./GuardianPriorityBadge";
import DeadlineCountdown from "./DeadlineCountdown";
import WhyThisAlert from "./WhyThisAlert";
import AssigneePicker from "./qenva/AssigneePicker";
import WatchersStack from "./qenva/WatchersStack";
import CommentsThread from "./qenva/CommentsThread";
import ActivityTimeline from "./qenva/ActivityTimeline";
import ImpactEditor from "./ImpactEditor";

const IMPACT_KIND_TONES = {
  financial: "text-emerald-700 bg-emerald-50 border-emerald-200",
  legal: "text-rose-700 bg-rose-50 border-rose-200",
  operational: "text-sky-700 bg-sky-50 border-sky-200",
  business: "text-violet-700 bg-violet-50 border-violet-200",
};

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

export default function AlertDrawer({ alertId, onClose, onChanged }) {
  const { t } = useTranslation("alerts");
  const { user } = useAuth();
  const currentUserId = user && typeof user === "object" ? user.id : null;
  const navigate = useNavigate();
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeForm, setActiveForm] = useState(null); // "snooze" | "accept_risk" | "mute" | "resolve" | null
  const [submitting, setSubmitting] = useState(false);
  const [fixingNow, setFixingNow] = useState(false);

  useEffect(() => {
    if (!alertId) return;
    setLoading(true);
    api.get(`/alm/alerts/${alertId}`)
      .then((r) => setAlert(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load alert"))
      .finally(() => setLoading(false));
  }, [alertId]);

  // Close on ESC
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const refresh = async () => {
    const r = await api.get(`/alm/alerts/${alertId}`);
    setAlert(r.data);
    onChanged?.(r.data);
  };

  const callTransition = async (endpoint, payload, successMsg) => {
    setSubmitting(true);
    try {
      const method = endpoint.includes("/reopen") ? "post" : "patch";
      await api[method](endpoint, payload);
      toast.success(successMsg);
      setActiveForm(null);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setSubmitting(false);
    }
  };

  // Guardian C2 — "Fix now" handler: ask backend for the smartest next action,
  // then navigate + optionally pre-fill the chat composer.
  const fixNow = async () => {
    if (!alert) return;
    setFixingNow(true);
    try {
      const r = await api.post(`/alm/alerts/${alert.id}/resolve-action`);
      const plan = r.data || {};
      const ctxObj = plan.prefilled_context || {};
      const docId = ctxObj.document_id;
      // Persist any prefill so the target page can consume it on mount.
      if (plan.action_type === "open_chat" && ctxObj.prefilled_question && docId) {
        try {
          sessionStorage.setItem(
            `lg_chat_prefill_${docId}`,
            JSON.stringify({ question: ctxObj.prefilled_question }),
          );
        } catch { /* noop — private mode */ }
      }
      onClose?.();
      if (plan.redirect_url) {
        navigate(plan.redirect_url);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || t("toasts.action_failed"));
    } finally {
      setFixingNow(false);
    }
  };

  // ALM Multi-user — assignment, watcher management
  const handleAssign = async (userId) => {
    try {
      await api.patch(`/alm/alerts/${alert.id}/assign`, { assignee_user_id: userId });
      toast.success(userId ? t("assign.assigned_toast") : t("assign.unassigned_toast"));
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("assign.assign_failed"));
    }
  };

  const handleAddWatcher = async (userId) => {
    try {
      await api.post(`/alm/alerts/${alert.id}/watchers`, { user_id: userId });
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("watchers.follow_failed"));
    }
  };

  const handleRemoveWatcher = async (userId) => {
    try {
      await api.delete(`/alm/alerts/${alert.id}/watchers/${userId}`);
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("watchers.follow_failed"));
    }
  };

  if (!alertId) return null;

  return (
    <div className="fixed inset-0 z-50" data-testid="alert-drawer-root">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-zinc-950/40 backdrop-blur-sm"
        onClick={onClose}
        data-testid="alert-drawer-backdrop"
      />
      {/* Panel */}
      <aside
        className="absolute right-0 top-0 bottom-0 w-full max-w-xl bg-white shadow-2xl overflow-y-auto animate-slide-in-right"
        data-testid="alert-drawer-panel"
      >
        <header className="sticky top-0 bg-white border-b border-zinc-200 px-6 py-4 flex items-start justify-between gap-3 z-10">
          {loading || !alert ? (
            <div className="h-6 w-48 bg-zinc-100 animate-pulse rounded" />
          ) : (
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <GuardianPriorityBadge priority={alert.guardian_priority} size="md" />
                <AlertSeverityBadge severity={alert.severity} />
                <AlertStateBadge status={alert.status} />
                <AlertSourceBadge source_type={alert.source_type} />
                {alert.due_at && <DeadlineCountdown dueAt={alert.due_at} />}
              </div>
              <h2 className="font-display font-semibold text-base tracking-tight truncate" data-testid="alert-drawer-title">
                {alert.title}
              </h2>
            </div>
          )}
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-zinc-100 shrink-0"
            data-testid="alert-drawer-close"
          >
            <X size={18} />
          </button>
        </header>

        {loading || !alert ? (
          <div className="p-6 space-y-3">
            <div className="h-4 w-full bg-zinc-100 animate-pulse rounded" />
            <div className="h-4 w-3/4 bg-zinc-100 animate-pulse rounded" />
            <div className="h-32 w-full bg-zinc-50 animate-pulse rounded" />
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Urgency + Impact tiles */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="border border-zinc-200 rounded-lg p-4">
                <UrgencyMeter
                  score={alert.urgency_score}
                  signals={alert.urgency_signals}
                  testId="alert-drawer-urgency"
                />
              </div>
              <div className="border border-zinc-200 rounded-lg p-4" data-testid="alert-drawer-impact">
                <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-2">
                  Estimated impact
                </p>
                {alert.estimated_impact ? (
                  <>
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${IMPACT_KIND_TONES[alert.estimated_impact.kind] || ""}`}>
                        {alert.estimated_impact.kind}
                      </span>
                      {fmtMoney(alert.estimated_impact.amount, alert.estimated_impact.currency) && (
                        <span
                          className="font-mono tabular-nums text-sm font-semibold text-zinc-950 inline-flex items-center gap-1"
                          data-testid="alert-drawer-impact-amount"
                        >
                          <BadgeDollarSign size={12} />
                          {fmtMoney(alert.estimated_impact.amount, alert.estimated_impact.currency)}
                        </span>
                      )}
                      <span className="text-[10px] text-zinc-400 uppercase tracking-wider">
                        {alert.estimated_impact.source === "user" ? "User override" : "AI suggested"}
                      </span>
                    </div>
                    <p className="text-xs text-zinc-700 leading-snug">
                      {alert.estimated_impact.description}
                    </p>
                  </>
                ) : (
                  <p className="text-xs text-zinc-400 italic">Not estimated</p>
                )}
              </div>
            </div>

            {/* Summary */}
            <div data-testid="alert-drawer-summary">
              <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-2">Why</p>
              <p className="text-sm text-zinc-800 leading-relaxed whitespace-pre-wrap">{alert.summary}</p>
              <WhyThisAlert alert={alert} />
            </div>

            {/* Collaboration band — assignee + watchers */}
            <div
              className="border border-zinc-200 rounded-lg p-3 bg-white"
              data-testid="alert-drawer-collaboration"
            >
              <div className="flex items-center gap-2 mb-2.5">
                <Users size={13} className="text-qenva-blue" />
                <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-qenva-blue">
                  {t("collaboration.title")}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2.5">
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] uppercase tracking-[0.12em] text-zinc-500 font-semibold">
                    {t("collaboration.assignee_label")}
                  </span>
                  <AssigneePicker
                    currentAssignee={alert.assignee}
                    onAssign={handleAssign}
                    testId="alert-drawer-assignee"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] uppercase tracking-[0.12em] text-zinc-500 font-semibold">
                    {t("collaboration.watchers_label")}
                  </span>
                  <WatchersStack
                    watchers={alert.watchers_objects || []}
                    currentUserId={currentUserId}
                    onAdd={handleAddWatcher}
                    onRemove={handleRemoveWatcher}
                    testId="alert-drawer-watchers"
                  />
                </div>
              </div>
            </div>

            {/* Due / created */}
            <div className="grid sm:grid-cols-2 gap-4 text-xs">
              <div data-testid="alert-drawer-due">
                <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Due</p>
                <p className="font-mono tabular-nums text-zinc-900">{fmtTime(alert.due_at)}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Created</p>
                <p className="font-mono tabular-nums text-zinc-900">{fmtTime(alert.created_at)}</p>
              </div>
            </div>

            {/* Source navigation */}
            <SourceLinks alert={alert} navigate={navigate} onClose={onClose} />

            {/* Active state payload (snooze/acceptance/mute/resolution) */}
            <StatePayloadCard alert={alert} />

            {/* Action zone */}
            <div className="border-t border-zinc-100 pt-5" data-testid="alert-drawer-actions">
              <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-3">Actions</p>
              {alert.status === "superseded" ? (
                <p className="text-xs text-zinc-500 italic">
                  Superseded alerts are terminal — the underlying analytical concern is no longer detected.
                </p>
              ) : alert.status === "active" ? (
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    className="bg-zinc-950 hover:bg-zinc-800 text-white"
                    onClick={fixNow}
                    disabled={fixingNow}
                    data-testid="action-fix-now"
                  >
                    <Zap size={13} className="mr-1" />
                    {fixingNow ? t("actions.fix_now_loading") : t("actions.fix_now")}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setActiveForm("snooze")} data-testid="action-snooze">
                    <Clock size={13} className="mr-1" /> Snooze
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setActiveForm("accept_risk")} data-testid="action-accept-risk">
                    <ShieldCheck size={13} className="mr-1" /> Accept risk
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setActiveForm("mute")} data-testid="action-mute">
                    <EyeOff size={13} className="mr-1" /> Mute
                  </Button>
                  <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => setActiveForm("resolve")} data-testid="action-resolve">
                    <CheckCircle2 size={13} className="mr-1" /> Resolve
                  </Button>
                </div>
              ) : (
                <Button
                  size="sm"
                  className="bg-zinc-950 hover:bg-zinc-800 text-white"
                  onClick={() => callTransition(`/alm/alerts/${alert.id}/reopen`, { reason: "Reopened from drawer" }, "Alert reopened")}
                  data-testid="action-reopen"
                >
                  <RotateCw size={13} className="mr-1" /> Reopen
                </Button>
              )}

              {activeForm === "snooze" && (
                <SnoozeForm submitting={submitting} onCancel={() => setActiveForm(null)}
                  onSubmit={(p) => callTransition(`/alm/alerts/${alert.id}/snooze`, p, "Snoozed")}
                />
              )}
              {activeForm === "accept_risk" && (
                <AcceptRiskForm submitting={submitting} onCancel={() => setActiveForm(null)}
                  onSubmit={(p) => callTransition(`/alm/alerts/${alert.id}/accept_risk`, p, "Risk accepted")}
                />
              )}
              {activeForm === "mute" && (
                <MuteForm submitting={submitting} onCancel={() => setActiveForm(null)}
                  onSubmit={(p) => callTransition(`/alm/alerts/${alert.id}/mute`, p, "Muted")}
                />
              )}
              {activeForm === "resolve" && (
                <ResolveForm submitting={submitting} onCancel={() => setActiveForm(null)}
                  onSubmit={(p) => callTransition(`/alm/alerts/${alert.id}/resolve`, p, "Resolved")}
                />
              )}
            </div>

            {/* State history */}
            <div className="border-t border-zinc-100 pt-5" data-testid="alert-drawer-history">
              <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-3">History</p>
              <ul className="space-y-2">
                {[...(alert.state_history || [])].reverse().map((h, i) => (
                  <li key={i} className="text-xs flex items-start gap-2" data-testid={`alert-history-${i}`}>
                    <span className="mt-1 w-1.5 h-1.5 rounded-full bg-zinc-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-zinc-900">
                        {h.from_state ? <>{h.from_state} → </> : ""}
                        <span className="font-medium">{h.to_state}</span>
                        {h.system_event && (
                          <span className="ml-2 text-[10px] uppercase tracking-wider text-zinc-400 font-mono">
                            {h.system_event}
                          </span>
                        )}
                      </p>
                      {h.reason && <p className="text-zinc-500 truncate">{h.reason}</p>}
                      <p className="text-zinc-400 font-mono tabular-nums">{fmtTime(h.at)}</p>
                    </div>
                  </li>
                ))}
              </ul>

              {/* Estimated impact — editable via PATCH /alm/alerts/{id}/impact */}
              <ImpactEditor
                alert={alert}
                onUpdated={(fresh) => {
                  setAlert(fresh);
                  onChanged?.();
                }}
              />
            </div>

            {/* Comments — multi-user collaborative thread */}
            <div className="border-t border-zinc-100 pt-5">
              <CommentsThread
                alertId={alert.id}
                currentUserId={currentUserId}
                testId="alert-drawer-comments"
              />
            </div>

            {/* Activity timeline — merged event stream */}
            <div className="border-t border-zinc-100 pt-5">
              <ActivityTimeline
                alertId={alert.id}
                testId="alert-drawer-activity"
              />
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}

// -- Sub-components ---------------------------------------------------------
function SourceLinks({ alert, navigate, onClose }) {
  const ref = alert.source_ref || {};
  const handleNav = (path) => { onClose?.(); navigate(path); };
  return (
    <div className="space-y-1" data-testid="alert-drawer-source-links">
      <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-2">Linked context</p>
      <ul className="text-xs space-y-1.5">
        {ref.document_id && (
          <li>
            <button
              onClick={() => handleNav(`/analysis/${ref.document_id}`)}
              className="inline-flex items-center gap-1.5 text-zinc-700 hover:text-zinc-950 hover:underline"
              data-testid="source-link-document"
            >
              <FileText size={12} />
              Source document <ExternalLink size={10} />
            </button>
          </li>
        )}
        {ref.comparison_id && (
          <li>
            <button
              onClick={() => handleNav(`/comparison/${ref.comparison_id}`)}
              className="inline-flex items-center gap-1.5 text-zinc-700 hover:text-zinc-950 hover:underline"
              data-testid="source-link-comparison"
            >
              <ScanLine size={12} />
              Comparison findings <ExternalLink size={10} />
            </button>
          </li>
        )}
        {ref.decision_id && (
          <li>
            <button
              onClick={() => handleNav(`/decision/${ref.decision_id}`)}
              className="inline-flex items-center gap-1.5 text-zinc-700 hover:text-zinc-950 hover:underline"
              data-testid="source-link-decision"
            >
              <ScanLine size={12} />
              Decision context <ExternalLink size={10} />
            </button>
          </li>
        )}
        {!ref.document_id && !ref.comparison_id && !ref.decision_id && (
          <li className="text-zinc-400 italic">No linked entities (manual alert)</li>
        )}
      </ul>
    </div>
  );
}

function StatePayloadCard({ alert }) {
  if (alert.status === "snoozed" && alert.snooze) {
    return (
      <div className="border border-zinc-200 rounded-lg p-3 bg-zinc-50" data-testid="state-payload-snooze">
        <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Snoozed until</p>
        <p className="font-mono text-sm text-zinc-900">{fmtTime(alert.snooze.until)}</p>
        {alert.snooze.reason && <p className="text-xs text-zinc-600 mt-1">{alert.snooze.reason}</p>}
      </div>
    );
  }
  if (alert.status === "accepted_risk" && alert.acceptance) {
    return (
      <div className="border border-violet-200 rounded-lg p-3 bg-violet-50/40" data-testid="state-payload-acceptance">
        <p className="text-[11px] uppercase tracking-wider text-violet-700 mb-1">Risk accepted — justification</p>
        <p className="text-sm text-zinc-900">{alert.acceptance.justification}</p>
        {alert.acceptance.review_at && (
          <p className="text-xs text-zinc-600 mt-1">Review by {fmtTime(alert.acceptance.review_at)}</p>
        )}
        {alert.acceptance.conditions?.length > 0 && (
          <ul className="text-xs text-zinc-700 mt-1 list-disc list-inside">
            {alert.acceptance.conditions.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        )}
      </div>
    );
  }
  if (alert.status === "muted" && alert.mute) {
    return (
      <div className="border border-zinc-200 rounded-lg p-3 bg-zinc-50" data-testid="state-payload-mute">
        <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Muted</p>
        {alert.mute.reason && <p className="text-xs text-zinc-600">{alert.mute.reason}</p>}
      </div>
    );
  }
  if (alert.status === "resolved" && alert.resolution) {
    return (
      <div className="border border-emerald-200 rounded-lg p-3 bg-emerald-50/40" data-testid="state-payload-resolution">
        <p className="text-[11px] uppercase tracking-wider text-emerald-700 mb-1">Resolution</p>
        <p className="text-sm text-zinc-900">{alert.resolution.note}</p>
      </div>
    );
  }
  if (alert.status === "superseded" && alert.supersession) {
    return (
      <div className="border border-zinc-200 rounded-lg p-3 bg-zinc-50" data-testid="state-payload-supersession">
        <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Superseded</p>
        <p className="text-xs text-zinc-600">{alert.supersession.reason}</p>
      </div>
    );
  }
  return null;
}

// -- Action forms -----------------------------------------------------------
function SnoozeForm({ onSubmit, onCancel, submitting }) {
  const [until, setUntil] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 14);
    return d.toISOString().slice(0, 16); // local input format
  });
  const [reason, setReason] = useState("");
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSubmit({ until: new Date(until).toISOString(), reason: reason || null }); }}
      className="mt-3 p-3 border border-zinc-200 rounded-lg bg-zinc-50 space-y-2"
      data-testid="snooze-form"
    >
      <label className="block text-xs">
        <span className="text-zinc-600">Snooze until</span>
        <input
          type="datetime-local" value={until} onChange={(e) => setUntil(e.target.value)} required
          className="w-full mt-1 text-sm border border-zinc-200 rounded px-2 py-1"
          data-testid="snooze-until"
        />
      </label>
      <textarea
        placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)}
        className="w-full text-sm border border-zinc-200 rounded px-2 py-1 min-h-[60px]"
        data-testid="snooze-reason"
      />
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={submitting} className="bg-zinc-950 text-white" data-testid="snooze-submit">Snooze</Button>
        <Button type="button" size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
}

function AcceptRiskForm({ onSubmit, onCancel, submitting }) {
  const [justification, setJustification] = useState("");
  const [reviewAt, setReviewAt] = useState("");
  const [conditions, setConditions] = useState("");
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          justification,
          review_at: reviewAt ? new Date(reviewAt).toISOString() : null,
          conditions: conditions ? conditions.split("\n").map((c) => c.trim()).filter(Boolean) : null,
        });
      }}
      className="mt-3 p-3 border border-violet-200 rounded-lg bg-violet-50/30 space-y-2"
      data-testid="accept-risk-form"
    >
      <textarea
        placeholder="Justification (≥ 10 chars, required — appears in compliance register)"
        value={justification} onChange={(e) => setJustification(e.target.value)}
        required minLength={10}
        className="w-full text-sm border border-zinc-200 rounded px-2 py-1 min-h-[80px]"
        data-testid="accept-risk-justification"
      />
      <label className="block text-xs">
        <span className="text-zinc-600">Review by (optional)</span>
        <input
          type="datetime-local" value={reviewAt} onChange={(e) => setReviewAt(e.target.value)}
          className="w-full mt-1 text-sm border border-zinc-200 rounded px-2 py-1"
          data-testid="accept-risk-review-at"
        />
      </label>
      <textarea
        placeholder="Conditions (one per line, optional)"
        value={conditions} onChange={(e) => setConditions(e.target.value)}
        className="w-full text-sm border border-zinc-200 rounded px-2 py-1 min-h-[50px]"
        data-testid="accept-risk-conditions"
      />
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={submitting} className="bg-violet-700 hover:bg-violet-800 text-white" data-testid="accept-risk-submit">Accept risk</Button>
        <Button type="button" size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
}

function MuteForm({ onSubmit, onCancel, submitting }) {
  const [reason, setReason] = useState("");
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSubmit({ reason: reason || null }); }}
      className="mt-3 p-3 border border-zinc-200 rounded-lg bg-zinc-50 space-y-2"
      data-testid="mute-form"
    >
      <textarea
        placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)}
        className="w-full text-sm border border-zinc-200 rounded px-2 py-1 min-h-[60px]"
        data-testid="mute-reason"
      />
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={submitting} className="bg-zinc-950 text-white" data-testid="mute-submit">Mute</Button>
        <Button type="button" size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
}

function ResolveForm({ onSubmit, onCancel, submitting }) {
  const [note, setNote] = useState("");
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSubmit({ note }); }}
      className="mt-3 p-3 border border-emerald-200 rounded-lg bg-emerald-50/30 space-y-2"
      data-testid="resolve-form"
    >
      <textarea
        placeholder="Resolution note (required) — e.g. 'replaced policy with $2M umbrella, doc uploaded'"
        value={note} onChange={(e) => setNote(e.target.value)} required
        className="w-full text-sm border border-zinc-200 rounded px-2 py-1 min-h-[80px]"
        data-testid="resolve-note"
      />
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={submitting} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="resolve-submit">Mark resolved</Button>
        <Button type="button" size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
}
